import React, { useState, useEffect } from 'react';
import chatApi from '../../services/chatApi';
import useAuthStore from '../../store/useAuthStore';
import { useToast } from '../ToastProvider';
import './GroupManagementModal.css';

const MAX_PHOTO_SIZE_BYTES = 500 * 1024 * 1024;

const GroupManagementModal = ({ isOpen, onClose, group, onUpdate, onDelete, onLeave }) => {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState('info'); // 'info', 'members'
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    avatar: null
  });
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCreator, setIsCreator] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const { showToast, confirm } = useToast();

  useEffect(() => {
    if (isOpen && group) {
      loadGroupDetails();
      loadAllUsers();
    }
  }, [isOpen, group]);

  const loadGroupDetails = async () => {
    if (!group) return;
    
    setLoadingData(true);
    try {
      const details = await chatApi.getGroupDetails(group.id);
      
      // Проверяем, является ли текущий пользователь админом или создателем
      // Получаем ID текущего пользователя из разных возможных источников
      let currentUserId = null;
      if (user?.userId) {
        currentUserId = user.userId;
      } else if (user?.id) {
        currentUserId = user.id;
      } else {
        // Пробуем получить из API
        try {
          const me = await chatApi.getMe();
          currentUserId = me.id || me.userId;
        } catch (err) {
          console.error('Failed to get current user:', err);
        }
      }
      
      if (currentUserId) {
        const currentMember = details.members?.find(m => m.id === currentUserId || m.id === parseInt(currentUserId));
        setIsAdmin(currentMember?.isAdmin || false);
        setIsCreator(details.createdBy === currentUserId || details.createdBy === parseInt(currentUserId));
      }
      
      setFormData({
        name: details.name || '',
        description: details.description || '',
        avatar: details.avatar || null
      });
      setAvatarPreview(details.avatar || null);
      
      if (details.members) {
        setMembers(details.members);
      }
    } catch (err) {
      console.error('Failed to load group details:', err);
      setError('Не удалось загрузить информацию о группе');
    } finally {
      setLoadingData(false);
    }
  };

  const loadAllUsers = async () => {
    try {
      const users = await chatApi.getUsers();
      setAllUsers(users);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_PHOTO_SIZE_BYTES) {
        setError('Размер файла не должен превышать 500 МБ');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setAvatarPreview(base64);
        setFormData({ ...formData, avatar: base64 });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateGroup = async (e) => {
    e.preventDefault();
    if (!isAdmin) {
      setError('Только администраторы могут редактировать группу');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const updated = await chatApi.updateGroup(group.id, {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        avatar: formData.avatar || null
      });

      if (onUpdate) {
        onUpdate(updated);
      }
      
      await loadGroupDetails();
      onClose();
    } catch (err) {
      setError(err.message || 'Ошибка при обновлении группы');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMembers = async (memberIds) => {
    if (!isAdmin) {
      setError('Только администраторы могут добавлять участников');
      return;
    }

    setLoading(true);
    try {
      await chatApi.addGroupMembers(group.id, memberIds);
      await loadGroupDetails();
      setShowAddMembers(false);
    } catch (err) {
      setError(err.message || 'Ошибка при добавлении участников');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!isAdmin) {
      setError('Только администраторы могут удалять участников');
      return;
    }

    const ok = await confirm({
      title: 'Удалить участника',
      message: 'Вы уверены, что хотите удалить этого участника из группы?',
      confirmText: 'Удалить',
      cancelText: 'Отмена',
    });
    if (!ok) return;

    setLoading(true);
    try {
      await chatApi.removeGroupMember(group.id, memberId);
      await loadGroupDetails();
    } catch (err) {
      setError(err.message || 'Ошибка при удалении участника');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async () => {
    setLoading(true);
    try {
      await chatApi.deleteGroup(group.id);
      if (onDelete) {
        onDelete();
      }
      onClose();
    } catch (err) {
      setError(err.message || 'Ошибка при удалении группы');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleLeaveGroup = async () => {
    setLoading(true);
    try {
      await chatApi.leaveGroup(group.id);
      if (onLeave) {
        onLeave();
      }
      onClose();
    } catch (err) {
      setError(err.message || 'Ошибка при выходе из группы');
    } finally {
      setLoading(false);
      setShowLeaveConfirm(false);
    }
  };

  const handleCopyLink = () => {
    const link = `${window.location.origin}/chat/group/${group.id}`;
    navigator.clipboard.writeText(link).then(() => {
      showToast('Ссылка скопирована в буфер обмена!', { type: 'success' });
    }).catch(() => {
      // Fallback для старых браузеров
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showToast('Ссылка скопирована в буфер обмена!', { type: 'success' });
    });
  };

  const handleClose = () => {
    setActiveTab('info');
    setShowAddMembers(false);
    setShowDeleteConfirm(false);
    setShowLeaveConfirm(false);
    setError(null);
    onClose();
  };

  const availableUsers = allUsers.filter(user => 
    !members.some(m => m.id === user.id)
  );

  const filteredAvailableUsers = availableUsers.filter(user => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const displayName = (user.displayName || user.fullName || user.username || '').toLowerCase();
    const username = (user.username || '').toLowerCase();
    return displayName.includes(query) || username.includes(query);
  });

  const [selectedMemberIds, setSelectedMemberIds] = useState([]);

  const toggleMemberSelection = (userId) => {
    setSelectedMemberIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  if (!isOpen || !group) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content management-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Управление группой</h2>
          <button className="modal-close" onClick={handleClose}>×</button>
        </div>
        
        <div className="modal-tabs">
          <button
            className={activeTab === 'info' ? 'active' : ''}
            onClick={() => setActiveTab('info')}
          >
            Информация
          </button>
          <button
            className={activeTab === 'members' ? 'active' : ''}
            onClick={() => setActiveTab('members')}
          >
            Участники ({members.length})
          </button>
        </div>

        {loadingData ? (
          <div className="loading-state">Загрузка...</div>
        ) : (
          <>
            {activeTab === 'info' && (
              <form onSubmit={handleUpdateGroup} className="modal-form">
                {error && <div className="error-message">{error}</div>}
                
                {!isAdmin && (
                  <div className="info-message">
                    Только администраторы могут редактировать информацию о группе
                  </div>
                )}
                
                <div className="form-group">
                  <label>Аватар группы</label>
                  <div className="avatar-upload">
                    <div className="avatar-preview">
                      {avatarPreview ? (
                        <img src={avatarPreview} alt="Preview" />
                      ) : (
                        <div className="avatar-placeholder">👥</div>
                      )}
                    </div>
                    {isAdmin && (
                      <>
                        <label className="avatar-upload-button">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleAvatarChange}
                            style={{ display: 'none' }}
                          />
                          {avatarPreview ? 'Изменить' : 'Загрузить аватар'}
                        </label>
                        {avatarPreview && (
                          <button
                            type="button"
                            className="remove-avatar-button"
                            onClick={() => {
                              setAvatarPreview(null);
                              setFormData({ ...formData, avatar: null });
                            }}
                          >
                            Удалить
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Название группы *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Введите название группы"
                    required
                    maxLength={100}
                    disabled={!isAdmin}
                  />
                </div>

                <div className="form-group">
                  <label>Описание</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Введите описание группы"
                    rows={4}
                    maxLength={500}
                    disabled={!isAdmin}
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className="btn-copy-link"
                    title="Скопировать ссылку на группу"
                  >
                    📋 Копировать ссылку
                  </button>
                </div>

                {isAdmin && (
                  <div className="form-actions">
                    <button type="button" onClick={handleClose} className="btn-cancel">
                      Отмена
                    </button>
                    <button type="submit" disabled={loading} className="btn-submit">
                      {loading ? 'Сохранение...' : 'Сохранить изменения'}
                    </button>
                  </div>
                )}

                <div className="danger-zone">
                  <h3>Опасная зона</h3>
                  {isCreator ? (
                    <>
                      <p>Вы являетесь создателем этой группы. Вы можете удалить её.</p>
                      {!showDeleteConfirm ? (
                        <button
                          type="button"
                          onClick={() => setShowDeleteConfirm(true)}
                          className="btn-danger"
                        >
                          Удалить группу
                        </button>
                      ) : (
                        <div className="confirm-delete">
                          <p>Вы уверены, что хотите удалить группу? Это действие нельзя отменить.</p>
                          <div className="confirm-actions">
                            <button
                              type="button"
                              onClick={() => setShowDeleteConfirm(false)}
                              className="btn-cancel"
                            >
                              Отмена
                            </button>
                            <button
                              type="button"
                              onClick={handleDeleteGroup}
                              disabled={loading}
                              className="btn-danger"
                            >
                              {loading ? 'Удаление...' : 'Да, удалить'}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <p>Вы можете выйти из этой группы.</p>
                      {!showLeaveConfirm ? (
                        <button
                          type="button"
                          onClick={() => setShowLeaveConfirm(true)}
                          className="btn-danger-outline"
                        >
                          Выйти из группы
                        </button>
                      ) : (
                        <div className="confirm-delete">
                          <p>Вы уверены, что хотите выйти из группы?</p>
                          <div className="confirm-actions">
                            <button
                              type="button"
                              onClick={() => setShowLeaveConfirm(false)}
                              className="btn-cancel"
                            >
                              Отмена
                            </button>
                            <button
                              type="button"
                              onClick={handleLeaveGroup}
                              disabled={loading}
                              className="btn-danger-outline"
                            >
                              {loading ? 'Выход...' : 'Да, выйти'}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </form>
            )}

            {activeTab === 'members' && (
              <div className="modal-form">
                {error && <div className="error-message">{error}</div>}
                
                {isAdmin && !showAddMembers && (
                  <div className="form-actions-top">
                    <button
                      type="button"
                      onClick={() => setShowAddMembers(true)}
                      className="btn-add-members"
                    >
                      + Добавить участников
                    </button>
                  </div>
                )}

                {showAddMembers ? (
                  <div className="add-members-section">
                    <div className="add-members-header">
                      <h3>Добавить участников</h3>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddMembers(false);
                          setSelectedMemberIds([]);
                          setSearchQuery('');
                        }}
                        className="btn-cancel-small"
                      >
                        Отмена
                      </button>
                    </div>
                    <div className="form-group">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Поиск пользователей..."
                        className="member-search"
                      />
                    </div>
                    <div className="members-list">
                      {filteredAvailableUsers.length === 0 ? (
                        <div className="empty-members">Нет доступных пользователей</div>
                      ) : (
                        filteredAvailableUsers.map(user => (
                          <div
                            key={user.id}
                            className={`member-item ${selectedMemberIds.includes(user.id) ? 'selected' : ''}`}
                            onClick={() => toggleMemberSelection(user.id)}
                          >
                            <div className="member-avatar">
                              {user.avatar ? (
                                <img src={user.avatar} alt="Avatar" />
                              ) : (
                                <span>{((user.displayName || user.fullName || user.username || '?')[0] || '?').toUpperCase()}</span>
                              )}
                            </div>
                            <div className="member-info">
                              <div className="member-name">
                                {user.displayName || user.fullName || user.username}
                              </div>
                              {user.username && (
                                <div className="member-username">@{user.username}</div>
                              )}
                            </div>
                            {selectedMemberIds.includes(user.id) && (
                              <div className="member-check">✓</div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                    {selectedMemberIds.length > 0 && (
                      <div className="form-actions">
                        <button
                          type="button"
                          onClick={() => {
                            setShowAddMembers(false);
                            setSelectedMemberIds([]);
                            setSearchQuery('');
                          }}
                          className="btn-cancel"
                        >
                          Отмена
                        </button>
                        <button
                          type="button"
                          onClick={() => handleAddMembers(selectedMemberIds)}
                          disabled={loading}
                          className="btn-submit"
                        >
                          {loading ? 'Добавление...' : `Добавить (${selectedMemberIds.length})`}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="form-group">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Поиск участников..."
                        className="member-search"
                      />
                    </div>
                    <div className="members-list">
                      {members.length === 0 ? (
                        <div className="empty-members">Нет участников</div>
                      ) : (
                        members
                          .filter(member => {
                            if (!searchQuery.trim()) return true;
                            const query = searchQuery.toLowerCase();
                            const name = ((member.displayName || member.username || '') || '').toLowerCase();
                            return name.includes(query);
                          })
                          .map(member => (
                            <div key={member.id} className="member-item">
                              <div className="member-avatar">
                                {member.avatar ? (
                                  <img src={member.avatar} alt="Avatar" />
                                ) : (
                                  <span>{((member.displayName || member.username || '?')[0] || '?').toUpperCase()}</span>
                                )}
                              </div>
                              <div className="member-info">
                                <div className="member-name">
                                  {member.displayName || member.username}
                                </div>
                                {member.username && (
                                  <div className="member-username">@{member.username}</div>
                                )}
                              </div>
                              {member.isAdmin && (
                                <div className="admin-badge">Админ</div>
                              )}
                              {isAdmin && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveMember(member.id)}
                                  className="remove-member-button"
                                  title="Удалить участника"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          ))
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GroupManagementModal;
